// pages/whatsapp/Templates.jsx
import React, { useEffect, useState } from "react";
import axios from "../../../utils/axios";
import { useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiCopy,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiRefreshCw,
  FiAlertCircle,
  FiInfo,
} from "react-icons/fi";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [syncingId, setSyncingId] = useState(null);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  /* ── Toast ── */
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  /* ── Load templates ── */
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (categoryFilter) params.append("category", categoryFilter);
      const res = await axios.get(`/api/templates?${params.toString()}`);
      if (res.data.success) setTemplates(res.data.data);
      else if (Array.isArray(res.data)) setTemplates(res.data);
      else setTemplates([]);
    } catch {
      showToast("Failed to load templates", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [searchTerm, categoryFilter]);

  /* ── Manual sync from Meta ── */
  const handleSyncStatus = async (template) => {
    if (!template.metaTemplateId) {
      showToast("Template not submitted to Meta yet", "error");
      return;
    }
    setSyncingId(template._id);
    try {
      const res = await axios.post(
        `/api/templates/${template._id}/sync-status`,
      );
      if (res.data.success) {
        showToast(res.data.message || "Status synced ✓");
        loadTemplates();
      } else {
        showToast(res.data.error || "Sync failed", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.error || "Sync failed", "error");
    } finally {
      setSyncingId(null);
    }
  };

  /* ── Delete ── */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?"))
      return;
    try {
      await axios.delete(`/api/templates/${id}`);
      showToast("Template deleted");
      loadTemplates();
    } catch {
      showToast("Failed to delete template", "error");
    }
  };

  /* ── Copy ── */
  const handleCopy = async (id) => {
    try {
      await axios.post(`/api/templates/${id}/copy`);
      showToast("Template copied as Draft ✓");
      loadTemplates();
    } catch {
      showToast("Failed to copy template", "error");
    }
  };

  const formatDate = (d) =>
    new Date(d)
      .toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(/\//g, "-");

  /* ── Style helpers ── */
  const getStatusStyle = (status) =>
    ({
      Approved: "bg-green-100 text-green-700 border border-green-200",
      "Pending Approval":
        "bg-yellow-100 text-yellow-700 border border-yellow-200",
      Rejected: "bg-red-100 text-red-700 border border-red-200",
      Draft: "bg-gray-100 text-gray-600 border border-gray-200",
    })[status] || "bg-gray-100 text-gray-600 border border-gray-200";

  const getStatusDot = (status) =>
    ({
      Approved: "bg-green-500",
      "Pending Approval": "bg-yellow-400 animate-pulse",
      Rejected: "bg-red-500",
      Draft: "bg-gray-400",
    })[status] || "bg-gray-400";

  const getCategoryColor = (cat) =>
    ({
      Marketing: "bg-green-100 text-green-800",
      Utility: "bg-blue-100 text-blue-800",
    })[cat] || "bg-gray-100 text-gray-800";

  return (
    <div className="p-4 md:p-6 min-h-screen bg-gray-50">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-5">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">
            WhatsApp Templates
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Create & submit templates to Meta for approval
          </p>
        </div>
        <button
          onClick={() => navigate("/whatsapp/templates/create")}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium shadow-sm transition-colors"
        >
          <FiPlus size={16} /> Create New Template
        </button>
      </div>

      {/* ── Info banner ── */}
      <div className="mb-5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex gap-3">
        <FiInfo size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-800">
            How approval works
          </p>
          <p className="text-xs text-blue-600 mt-0.5 leading-relaxed">
            Click <strong>"Send For Approval"</strong> → Template auto-submits
            to Meta → Meta reviews in <strong>24–48 hours</strong> → Click{" "}
            <strong>↻ Sync</strong> button to check latest status from Meta.
          </p>
        </div>
      </div>

      {/* ── Search & Filter ── */}
      <div className="mb-5 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <FiSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={16}
          />
          <input
            type="text"
            placeholder="Search templates..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm bg-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="w-full sm:w-48 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm bg-white"
          value={categoryFilter}
          onChange={(e) =>
            setCategoryFilter(e.target.value === "All" ? "" : e.target.value)
          }
        >
          {["All", "Marketing", "Utility"].map((c) => (
            <option key={c} value={c === "All" ? "" : c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                {[
                  "Template Name",
                  "Status",
                  "Meta ID",
                  "Category",
                  "Type",
                  "Dynamic",
                  "Created At",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-50">
              {/* Loading */}
              {loading && (
                <tr>
                  <td colSpan="8" className="py-10 text-center">
                    <div className="flex justify-center items-center gap-2 text-gray-400">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
                      <span className="text-sm">Loading templates...</span>
                    </div>
                  </td>
                </tr>
              )}

              {/* Empty */}
              {!loading && templates.length === 0 && (
                <tr>
                  <td colSpan="8" className="py-16 text-center">
                    <FiFileText
                      className="mx-auto text-gray-300 mb-3"
                      size={40}
                    />
                    <p className="text-gray-400 text-sm mb-4">
                      No templates found
                    </p>
                    <button
                      onClick={() => navigate("/whatsapp/templates/create")}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
                    >
                      Create your first template
                    </button>
                  </td>
                </tr>
              )}

              {/* Rows */}
              {!loading &&
                templates.map((t) => (
                  <tr
                    key={t._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {/* Name */}
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-800 whitespace-nowrap">
                        {t.templateName}
                      </p>
                      {t.metaError && (
                        <p className="text-xs text-red-500 mt-0.5 flex items-center gap-1">
                          <FiAlertCircle size={10} />
                          Meta error — retry approval
                        </p>
                      )}
                      {t.metaSyncedAt && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          Synced: {formatDate(t.metaSyncedAt)}
                        </p>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusDot(t.status)}`}
                        />
                        <span
                          className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusStyle(t.status)}`}
                        >
                          {t.status}
                        </span>
                      </div>
                      {t.rejectionReason && t.rejectionReason !== "NONE" && (
                        <p
                          className="text-xs text-red-500 mt-1 max-w-[160px] truncate"
                          title={t.rejectionReason}
                        >
                          ↳ {t.rejectionReason}
                        </p>
                      )}
                    </td>

                    {/* Meta ID */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {t.metaTemplateId ? (
                        <span className="text-xs font-mono bg-teal-50 text-teal-700 px-2 py-1 rounded-lg">
                          {t.metaTemplateId}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">
                          Not submitted
                        </span>
                      )}
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2.5 py-1 text-xs font-medium rounded-full ${getCategoryColor(t.category)}`}
                      >
                        {t.category}
                      </span>
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {t.templateType}
                    </td>

                    {/* Dynamic */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2.5 py-1 text-xs font-medium rounded-full ${t.dynamicContent ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}
                      >
                        {t.dynamicContent ? "Yes" : "No"}
                      </span>
                    </td>

                    {/* Created At */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-400">
                      {formatDate(t.createdAt)}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {/* View */}
                        <button
                          onClick={() =>
                            navigate(`/whatsapp/templates/view/${t._id}`)
                          }
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View"
                        >
                          <FiEye size={15} />
                        </button>

                        {/* Edit */}
                        <button
                          onClick={() =>
                            navigate(`/whatsapp/templates/edit/${t._id}`)
                          }
                          className="p-1.5 text-teal-500 hover:bg-teal-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 size={15} />
                        </button>

                        {/* ★ Sync status from Meta — only if submitted */}
                        {t.metaTemplateId && (
                          <button
                            onClick={() => handleSyncStatus(t)}
                            disabled={syncingId === t._id}
                            className="p-1.5 text-purple-500 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-40"
                            title="Sync status from Meta"
                          >
                            <FiRefreshCw
                              size={14}
                              className={
                                syncingId === t._id ? "animate-spin" : ""
                              }
                            />
                          </button>
                        )}

                        {/* Copy */}
                        <button
                          onClick={() => handleCopy(t._id)}
                          className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                          title="Copy"
                        >
                          <FiCopy size={15} />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(t._id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {!loading && templates.length > 0 && (
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-100 flex justify-between items-center">
            <span className="text-xs text-gray-500">
              Showing{" "}
              <span className="font-semibold text-gray-700">
                {templates.length}
              </span>{" "}
              templates
            </span>
            <div className="flex gap-1">
              <button
                className="p-1.5 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
                disabled
              >
                <FiChevronLeft size={14} />
              </button>
              <button
                className="p-1.5 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
                disabled
              >
                <FiChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium border animate-fade-in
          ${
            toast.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          <span>{toast.type === "success" ? "✓" : "✕"}</span>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
